import React, { useState, useEffect, useRef } from "react";
import {
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Loader2,
  MessageCircle,
  Mic,
  Package,
  Pencil,
  Plus,
  Printer,
  Receipt,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Timer,
  Trash2,
  Truck,
  Wrench,
  XCircle,
} from "lucide-react";
import { abrirWhatsApp, Btn, Busca, CabecalhoFolha, Field, ItensFolha, Modal, Pill, Placa, Vazio, usarServidor } from "../ui/componentes";
import { FORMAS_ESCOLHIVEIS, ROTULO_FORMA, ST, atrasado, brl, fdata, num } from "../ui/formato";
import { inteiroBR, numeroBR } from "../numero";

/* As etapas na ordem em que a oficina anda. "recusada" fica de fora de
   propósito: é saída lateral, não degrau — mostrá-la na régua sugeriria que
   toda OS passa por lá. */
/* Os rótulos e o que é escolhível moram em ui/formato.js, junto do enum do
   banco que eles espelham. Esta tela e o financeiro liam mapas separados, e
   foi assim que o financeiro acabou oferecendo um "cartao" que o banco não
   aceita. Aqui só o formato de par que o <select> consome. */
const FORMAS = FORMAS_ESCOLHIVEIS.map((v) => [v, ROTULO_FORMA[v]]);
/* Parcelar só faz sentido onde a oficina realmente parcela. Dinheiro e pix
   em três vezes é combinado de boca, não título a receber. */
const PARCELAVEIS = ["credito", "boleto", "crediario"];
/* Formas que se resolvem no balcão: quando o carro sai, o dinheiro está na
   mão. Cartão de crédito fica de fora de propósito — o cliente pagou, mas
   quem ainda deve à oficina é a operadora, e isso tem data própria. */
const NA_HORA = ["dinheiro", "pix", "debito", "transferencia"];

const ETAPAS = [
  ["orcamento", "Orçamento"],
  ["aguardando", "Aguardando cliente"],
  ["aprovada", "Aprovada"],
  ["execucao", "Em execução"],
  ["finalizada", "Finalizada"],
  ["entregue", "Entregue"],
];

/* ══════════════════════════════════════════════════════════════════
   DITADO POR VOZ — o mecânico fala, o orçamento se monta
   ══════════════════════════════════════════════════════════════════ */
function VozOrcamento({ open, onClose, db, aplicar, toast, mecanico }) {
  const [ouvindo, setOuvindo] = useState(false);
  const [texto, setTexto] = useState("");
  const [parcial, setParcial] = useState("");
  const [lendo, setLendo] = useState(false);
  const [saida, setSaida] = useState(null);
  const [marcados, setMarcados] = useState({});
  const rec = useRef(null);
  const suportado = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => { if (!open) { pararEscuta(); setTexto(""); setParcial(""); setSaida(null); setMarcados({}); } }, [open]);

  const pararEscuta = () => { try { rec.current && rec.current.stop(); } catch (e) {} rec.current = null; setOuvindo(false); setParcial(""); };

  const escutar = () => {
    if (ouvindo) return pararEscuta();
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast("Microfone indisponível", "Este navegador não reconhece voz. Digite o ditado no campo abaixo.", "alerta"); return; }
    const r = new SR();
    r.lang = "pt-BR"; r.continuous = true; r.interimResults = true;
    r.onresult = (e) => {
      let fin = "", inter = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) fin += t + " "; else inter += t;
      }
      if (fin) setTexto((s) => (s + " " + fin).trim());
      setParcial(inter);
    };
    r.onerror = () => { setOuvindo(false); toast("Não consegui ouvir", "Verifique a permissão do microfone no navegador.", "erro"); };
    r.onend = () => setOuvindo(false);
    rec.current = r; r.start(); setOuvindo(true);
  };

  /* leitura local — usada como rede de segurança se a IA falhar */
  const lerLocal = (t) => {
    const baixo = t.toLowerCase();
    const numPal = { um: 1, uma: 1, dois: 2, duas: 2, tres: 3, três: 3, quatro: 4, cinco: 5, seis: 6, oito: 8, dez: 10 };
    const qtdPerto = (idx) => {
      const antes = baixo.slice(Math.max(0, idx - 34), idx);
      const d = antes.match(/(\d+)\s*(un|litro|litros|peça|peças|jogo|jogos)?\s*$/);
      if (d) return +d[1];
      const p = Object.keys(numPal).find((k) => new RegExp(`\\b${k}\\b\\s*[a-zç ]{0,14}$`).test(antes));
      return p ? numPal[p] : 1;
    };
    const achar = (lista, campoValor) => lista.map((x) => {
      const chaves = x.nome.toLowerCase().replace(/[(),."]/g, " ").split(/\s+/).filter((w) => w.length > 4);
      const hit = chaves.filter((k) => baixo.includes(k.slice(0, 6)));
      if (hit.length < Math.max(1, Math.ceil(chaves.length * 0.34))) return null;
      const idx = baixo.indexOf(hit[0].slice(0, 6));
      return { ref: x.id, cod: x.cod, desc: x.nome, qtd: qtdPerto(idx), valor: x[campoValor] };
    }).filter(Boolean);
    const km = (baixo.match(/(\d{4,7})\s*(km|quil)/) || [])[1];
    return {
      km: km ? +km : null,
      queixa: t.split(/[.;]/)[0].slice(0, 160),
      itens: [...achar(db.pecas, "venda").map((x) => ({ ...x, tipo: "peca" })), ...achar(db.servicos, "valor").map((x) => ({ ...x, tipo: "servico" }))],
      observacoes: "Interpretado localmente, sem IA. Confira os valores.",
    };
  };

  const interpretar = async () => {
    const t = (texto + " " + parcial).trim();
    if (!t) { toast("Nada para interpretar", "Grave ou digite o ditado primeiro.", "alerta"); return; }
    pararEscuta(); setLendo(true);
    const catP = db.pecas.map((p) => `${p.id}|${p.cod}|${p.nome}|${p.venda}`).join("\n");
    const catS = db.servicos.map((s) => `${s.id}|${s.cod}|${s.nome}|${s.valor}`).join("\n");
    const prompt = `Você interpreta ditados de mecânicos de uma oficina brasileira e transforma em itens de orçamento.

PEÇAS EM ESTOQUE (id|código|nome|preço de venda):
${catP}

SERVIÇOS DA TABELA (id|código|nome|preço):
${catS}

DITADO DO MECÂNICO:
"""${t}"""

Regras:
- Se o item ditado corresponder a algo do catálogo, use exatamente o id, o nome e o preço do catálogo.
- Se não corresponder, use ref null e estime um preço realista em reais para o Sul de Minas.
- Quantidade padrão 1, salvo se o mecânico disser outra ("dois amortecedores", "8 litros de óleo").
- Óleo de motor é cobrado por litro.

Responda SOMENTE com JSON válido, sem markdown e sem explicação:
{"km": número ou null, "queixa": "resumo curto da queixa em português", "itens":[{"tipo":"peca"|"servico","ref":"id do catálogo ou null","desc":"descrição","qtd":número,"valor":número}], "observacoes":"recomendações técnicas ditadas"}`;

    try {
      const r = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1000, messages: [{ role: "user", content: prompt }] }),
      });
      const d = await r.json();
      const bruto = (d.content || []).map((c) => (c.type === "text" ? c.text : "")).join("").replace(/```json|```/g, "").trim();
      const j = JSON.parse(bruto.slice(bruto.indexOf("{"), bruto.lastIndexOf("}") + 1));
      const itens = (j.itens || []).map((i, k) => ({ ...i, k, valor: +i.valor || 0, qtd: +i.qtd || 1 }));
      setSaida({ ...j, itens });
      setMarcados(Object.fromEntries(itens.map((i) => [i.k, true])));
    } catch (e) {
      const j = lerLocal(t);
      const itens = j.itens.map((i, k) => ({ ...i, k }));
      setSaida({ ...j, itens });
      setMarcados(Object.fromEntries(itens.map((i) => [i.k, true])));
      toast("Interpretado sem IA", "A leitura assistida não respondeu. Confira os itens antes de aplicar.", "alerta");
    }
    setLendo(false);
  };

  const aplicarItens = () => {
    const escolhidos = (saida?.itens || []).filter((i) => marcados[i.k]);
    if (!escolhidos.length) { toast("Nenhum item marcado", "Marque ao menos um item para adicionar à OS.", "alerta"); return; }
    aplicar(escolhidos, saida);
    onClose();
    toast("Itens adicionados", `${escolhidos.length} item(ns) entraram no orçamento.`);
  };

  const totalMarcado = (saida?.itens || []).filter((i) => marcados[i.k]).reduce((s, i) => s + i.qtd * i.valor, 0);

  return (
    <Modal open={open} onClose={onClose} w="max-w-2xl" title="Ditar orçamento" sub={`Fale normalmente. ${mecanico?.nome || ""} — a OS registra quem ditou.`}
      footer={<>
        <Btn variant="g" onClick={onClose}>Fechar</Btn>
        {saida
          ? <><Btn variant="o" icon={RefreshCw} onClick={() => { setSaida(null); setTexto(""); }}>Refazer</Btn>
            <Btn variant="p" icon={Plus} onClick={aplicarItens}>Adicionar {brl(totalMarcado)} à OS</Btn></>
          : <Btn variant="p" icon={lendo ? Loader2 : Sparkles} onClick={interpretar} disabled={lendo}>{lendo ? "Interpretando…" : "Interpretar ditado"}</Btn>}
      </>}>
      {!saida ? (
        <div className="grid gap-4">
          <div className="flex flex-col items-center py-3">
            <button onClick={escutar} className={ouvindo ? "mic-ring" : ""}
              style={{ width: 78, height: 78, borderRadius: "50%", border: "none", cursor: "pointer", background: ouvindo ? "var(--signal)" : "var(--ink)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s" }}>
              <Mic size={31} strokeWidth={2.1} />
            </button>
            <div className="dsp mt-3" style={{ fontSize: 15 }}>{ouvindo ? "Ouvindo…" : "Toque para falar"}</div>
            <div className="text-xs t-mut2 mt-1 text-center" style={{ maxWidth: 380 }}>
              {suportado ? "Ex.: “Gol placa HKL8B21, 78 mil quilômetros. Trocar as duas pastilhas dianteiras e os dois discos, sangria de freio e óleo 5W30, quatro litros.”"
                : "Este navegador não reconhece voz. Digite o ditado abaixo — a leitura assistida funciona igual."}
            </div>
          </div>
          <Field label="Transcrição" hint="Você pode corrigir o texto antes de interpretar.">
            <textarea className="inp" rows={5} value={texto + (parcial ? " " + parcial : "")} onChange={(e) => { setTexto(e.target.value); setParcial(""); }}
              placeholder="O que for falado aparece aqui…" />
          </Field>
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
            <div className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}>
              <div className="eyebrow mb-1">Queixa identificada</div><div className="text-sm">{saida.queixa || "—"}</div>
            </div>
            <div className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}>
              <div className="eyebrow mb-1">Hodômetro ditado</div><div className="mono text-sm font-semibold">{saida.km ? `${num(saida.km)} km` : "não informado"}</div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="eyebrow">Itens reconhecidos</span>
              <span className="text-xs t-mut2">desmarque o que não vai entrar</span>
            </div>
            <div className="grid gap-1.5">
              {saida.itens.map((i) => (
                <label key={i.k} className="flex items-center gap-3 px-3 py-2.5" style={{ border: "1px solid var(--line)", borderRadius: "var(--r)", background: marcados[i.k] ? "var(--card)" : "var(--el)", cursor: "pointer" }}>
                  <input type="checkbox" checked={!!marcados[i.k]} onChange={(e) => setMarcados({ ...marcados, [i.k]: e.target.checked })} style={{ accentColor: "var(--ink)", width: 15, height: 15 }} />
                  <Pill t={i.tipo === "peca" ? "info" : "mut"}>{i.tipo === "peca" ? "Peça" : "Serviço"}</Pill>
                  <span className="flex-1 text-sm">{i.desc}{!i.ref && <span className="t-signal text-xs ml-2">fora do catálogo</span>}</span>
                  <span className="mono text-sm t-mut">{i.qtd}×</span>
                  <span className="mono text-sm font-semibold" style={{ width: 88, textAlign: "right" }}>{brl(i.qtd * i.valor)}</span>
                </label>
              ))}
              {saida.itens.length === 0 && <div className="text-sm t-mut2">Nada foi reconhecido no ditado. Tente falar o nome das peças e serviços com mais detalhe.</div>}
            </div>
          </div>
          {saida.observacoes && (
            <div className="p-3" style={{ background: "var(--gulf-l)", border: "1px solid #BEE0EC", borderRadius: "var(--r)" }}>
              <div className="eyebrow mb-1" style={{ color: "var(--gulf-d)" }}>Recomendações do mecânico</div>
              <div className="text-sm">{saida.observacoes}</div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════
   ORDENS DE SERVIÇO E ORÇAMENTOS
   ══════════════════════════════════════════════════════════════════ */
const totalOS = (o) => o.itens.reduce((s, i) => s + i.qtd * i.valor, 0) - (o.desconto || 0);
const custoOS = (o) => o.itens.reduce((s, i) => s + i.qtd * (i.custo || 0), 0);

function OrdensServico({ db, set, toast, usuario, aberta, setAberta, rascunho, setRascunho, emitirNF, acoes }) {
  /**
   * Abrir OS exige dizer para quem.
   *
   * O botão lia `db.clientes[0].id` direto. Numa oficina recém-instalada não
   * existe cliente nenhum, então `db.clientes[0]` é undefined e o `.id`
   * derrubava a tela — o primeiro clique do dono no primeiro dia.
   */
  /**
   * ABRIR OS NO BALCÃO
   *
   * Antes era preciso sair para Clientes, cadastrar a pessoa inteira, cadastrar
   * o veículo, e voltar. Com o carro no elevador e o cliente esperando, esse
   * caminho não é percorrido: o atendente cadastra às pressas, sem procurar
   * antes, e a mesma pessoa vira dois cadastros. Cliente duplicado quebra
   * histórico de veículo, garantia e cobrança.
   *
   * Aqui a busca vem PRIMEIRO e o cadastro aparece como saída de quem não
   * achou — que é a ordem que evita duplicata. O mínimo que o banco exige é
   * nome para o cliente e placa para o veículo; o resto entra depois, com
   * calma, na aba Clientes.
   */
  const [balcao, setBalcao] = useState(null);
  const b = balcao ?? {};
  const setB = (campos) => setBalcao({ ...b, ...campos });

  const clientesAchados = (() => {
    const q = (b.buscaCli ?? "").trim().toLowerCase();
    if (!q) return db.clientes.slice(0, 6);
    const so = (t) => (t ?? "").toLowerCase().replace(/\D/g, "");
    return db.clientes.filter((c) =>
      (c.nome ?? "").toLowerCase().includes(q) ||
      (so(c.tel) && so(c.tel).includes(q.replace(/\D/g, "")) && q.replace(/\D/g, ""))
    ).slice(0, 8);
  })();

  const veiculosDoCliente = b.cli ? db.veiculos.filter((v) => v.cli === b.cli.id) : [];

  const abrirBalcao = () => setBalcao({ buscaCli: "", cli: null, vei: null, tel: "", placa: "", modelo: "" });

  const criarClienteRapido = () => executar(
    () => acoes.criarCliente({ nome: (b.buscaCli ?? "").trim(), tel: b.tel }),
    (c) => { setB({ cli: c, buscaCli: c.nome }); toast("Cliente cadastrado", "Complete o cadastro depois, em Clientes."); },
    "Não deu para cadastrar o cliente");

  const criarVeiculoRapido = () => executar(
    () => acoes.criarVeiculo({ cli: b.cli.id, placa: b.placa, modelo: b.modelo }),
    (v) => { setB({ vei: v }); toast("Veículo cadastrado", "Marca, ano e cor podem entrar depois."); },
    "Não deu para cadastrar o veículo");

  const abrirOSdoBalcao = () => {
    const veiculo = b.vei ?? veiculosDoCliente[0];
    if (!b.cli || !veiculo) return;
    setBalcao(null);
    executar(
      () => acoes.abrirOS({ cli: b.cli.id, vei: veiculo.id,
                            mec: usuario.papel === "mecanico" ? usuario.id : null,
                            km: veiculo.km || 0, queixa: b.queixa || "" }),
      (o) => { setAberta(o._id); toast("OS aberta", `${o.id} criada. Monte o orçamento ou dite por voz.`); },
      "Não deu para abrir a OS");
  };


  const { executar, ocupado } = usarServidor(toast);
  const [filtro, setFiltro] = useState("todas");
  const [q, setQ] = useState("");
  const [voz, setVoz] = useState(false);
  const [addItem, setAddItem] = useState(null);
  const [buscaItem, setBuscaItem] = useState("");
  // "proprietario" é quem abriu a oficina — mesmo poder que admin. Sem
  // incluir aqui, o dono não enxergava importação, margem nem exclusão,
  // e o servidor aceitaria as três (exigirPapel já trata os dois iguais).
  const admin = ["admin", "proprietario"].includes(usuario.papel);

  const vei = (id) => db.veiculos.find((v) => v.id === id);
  const cli = (id) => db.clientes.find((c) => c.id === id);
  const os = aberta ? db.os.find((o) => o.id === aberta) : null;

  /**
   * Edição de campo: muda local na hora e salva depois de 800 ms parado.
   *
   * Uma requisição por tecla digitada na queixa seria absurdo — e sem o
   * salvamento, o texto some ao trocar de tela. O atraso resolve os dois.
   */
  const salvarDepois = useRef({});
  const mudarOS = (id, fn) => {
    const alvo = db.os.find((o) => o.id === id);
    /* Campo, não estado. O status tem rota própria no servidor — aprovar,
       recusar, executar, finalizar, entregar, reabrir — e a lista de cima
       não inclui status: mudá-lo por aqui só pinta a tela, o servidor não
       fica sabendo e o recarregamento seguinte desfaz. Foi assim que a OS
       parou de ser finalizada; que reclame alto se voltar a acontecer. */
    if (alvo && fn(alvo).status !== alvo.status)
      throw new Error("mudarOS não muda status: use a ação do servidor");
    set((d) => ({ ...d, os: d.os.map((o) => (o.id === id ? fn(o) : o)) }));
    if (!alvo?._id) return;
    clearTimeout(salvarDepois.current[id]);
    salvarDepois.current[id] = setTimeout(() => {
      const atual = fn(alvo);
      acoes.atualizarOS(alvo._id, {
        queixa: atual.queixa, km_entrada: inteiroBR(atual.km),
        data_previsao: atual.previsao || null, mecanico_id: atual.mec || null,
        /* A entrada faltava nesta lista. O campo existia na tela, mudava ao
           clicar e voltava sozinho no recarregamento seguinte — o servidor
           nunca soube. Mesma classe do status, achada ao conferir o resto. */
        data_abertura: atual.abertura || null,
        mecanico_anotado: atual.mecNome ?? "",
        desconto_valor: numeroBR(atual.desconto) ?? 0,
      }).catch(() => toast("Não salvou", "A alteração ficou só nesta tela. Verifique a conexão.", "alerta"));
    }, 800);
  };

  /* criar nova OS */
  useEffect(() => {
    if (!rascunho) return;
    const { cliId, veiId, ckId } = rascunho;
    const v = veiId || db.veiculos.find((x) => x.cli === cliId)?.id;
    setRascunho(null);
    if (!v) { toast("Sem veículo", "Cadastre o veículo do cliente antes de abrir a OS.", "erro"); return; }
    // O número da OS vem do servidor: gerar aqui daria números repetidos
    // assim que duas pessoas abrissem OS ao mesmo tempo.
    executar(
      () => acoes.abrirOS({ cli: cliId, vei: v, mec: usuario.papel === "mecanico" ? usuario.id : null,
                            km: vei(v)?.km || 0, queixa: "" }),
      (o) => { setAberta(o._id); toast("OS aberta", `${o.id} criada. Monte o orçamento ou dite por voz.`); },
      "Não deu para abrir a OS");
  }, [rascunho]);

  const ModalBalcao = () => (
    <Modal open={!!balcao} onClose={() => setBalcao(null)}
      title="Abrir ordem de serviço" sub="Procure o cliente antes de cadastrar — é o que evita o mesmo cliente virar dois."
      footer={
        <>
          <Btn variant="g" onClick={() => setBalcao(null)}>Cancelar</Btn>
          <Btn variant="p" icon={Wrench}
            disabled={!b.cli || !(b.vei ?? veiculosDoCliente[0])}
            onClick={abrirOSdoBalcao}>Abrir OS</Btn>
        </>
      }>
      <div className="grid gap-4">
        <div>
          <label className="lbl">1 · Cliente</label>
          <div className="relative">
            <Search size={15} className="absolute t-mut2" style={{ left: 10, top: 13 }} />
            <input className="inp" style={{ paddingLeft: 32 }} autoFocus
              placeholder="Nome ou telefone"
              value={b.buscaCli ?? ""}
              onChange={(e) => setB({ buscaCli: e.target.value, cli: null, vei: null })} />
          </div>

          {!b.cli && (
            <div className="grid gap-1 mt-2">
              {clientesAchados.map((c) => (
                <button key={c.id} className="card px-3 py-2 text-left flex items-center justify-between"
                  onClick={() => setB({ cli: c, buscaCli: c.nome, vei: null })}>
                  <span className="text-sm font-medium">{c.nome}</span>
                  <span className="text-xs t-mut mono">{c.tel || "sem telefone"}</span>
                </button>
              ))}
              {(b.buscaCli ?? "").trim().length >= 3 && clientesAchados.length === 0 && (
                <div className="card p-3 grid gap-2.5">
                  <div className="text-sm">
                    Nenhum cliente com esse nome. Cadastrar <strong>{b.buscaCli.trim()}</strong>?
                  </div>
                  <input className="inp inp-mono" placeholder="Telefone (opcional)"
                    value={b.tel ?? ""} inputMode="tel"
                    onChange={(e) => setB({ tel: e.target.value })} />
                  <div className="flex justify-end">
                    <Btn variant="p" size="xs" icon={Plus} onClick={criarClienteRapido}>Cadastrar cliente</Btn>
                  </div>
                  <div className="text-xs t-mut2">
                    Entra só com nome e telefone. CPF e endereço você completa depois, em Clientes.
                  </div>
                </div>
              )}
            </div>
          )}

          {b.cli && (
            <div className="flex items-center gap-2 mt-2 text-sm">
              <Check size={14} className="t-ok" />
              <span className="font-medium">{b.cli.nome}</span>
              <button className="btn btn-g btn-xs" onClick={() => setB({ cli: null, vei: null })}>trocar</button>
            </div>
          )}
        </div>

        {b.cli && (
          <div>
            <label className="lbl">2 · Veículo</label>
            {veiculosDoCliente.length > 0 && (
              <div className="grid gap-1 mb-2">
                {veiculosDoCliente.map((v) => (
                  <button key={v.id} className="card px-3 py-2 text-left flex items-center justify-between"
                    style={{ borderColor: (b.vei?.id ?? veiculosDoCliente[0].id) === v.id ? "var(--gulf)" : undefined }}
                    onClick={() => setB({ vei: v })}>
                    <Placa n={v.placa} size="sm" />
                    <span className="text-xs t-mut">{[v.marca, v.modelo].filter(Boolean).join(" ") || "sem modelo"}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="card p-3 grid gap-2.5">
              <div className="text-sm">
                {veiculosDoCliente.length ? "Ou cadastre outro veículo:" : "Este cliente ainda não tem veículo."}
              </div>
              <div className="grid gap-2" style={{ gridTemplateColumns: "140px 1fr" }}>
                <input className="inp inp-mono" placeholder="ABC1D23" maxLength={8}
                  style={{ textTransform: "uppercase" }}
                  value={b.placa ?? ""} onChange={(e) => setB({ placa: e.target.value })} />
                <input className="inp" placeholder="Modelo (opcional)"
                  value={b.modelo ?? ""} onChange={(e) => setB({ modelo: e.target.value })} />
              </div>
              <div className="flex justify-end">
                <Btn variant="o" size="xs" icon={Plus}
                  disabled={(b.placa ?? "").replace(/[^A-Za-z0-9]/g, "").length !== 7}
                  onClick={criarVeiculoRapido}>Cadastrar veículo</Btn>
              </div>
              <div className="text-xs t-mut2">Placa no padrão ABC1D23 ou ABC1234. Marca, ano e cor entram depois.</div>
            </div>
          </div>
        )}

        {b.cli && (b.vei ?? veiculosDoCliente[0]) && (
          <div>
            <label className="lbl">3 · Queixa do cliente (opcional)</label>
            <input className="inp" placeholder="O que ele relatou. Ex.: barulho na frente ao frear."
              value={b.queixa ?? ""} onChange={(e) => setB({ queixa: e.target.value })} />
          </div>
        )}
      </div>
    </Modal>
  );

  const addLinha = (item) => executar(
    () => acoes.addItemOS(os._id ?? os.id, item), null, "Não deu para adicionar o item");
  /**
   * Quantidade e valor de um item.
   *
   * Antes isto só mexia no estado local: não havia rota. O balconista corrigia
   * o preço de um item avulso, via o número certo na tela, e perdia tudo ao
   * recarregar — pior que não deixar editar, porque parece que salvou.
   *
   * A tela atualiza na hora e o servidor recebe depois de uma pausa: salvar a
   * cada tecla seria uma requisição por dígito digitado.
   */
  const salvarItemDepois = useRef({});
  /* O nome do campo na tela e no servidor não são o mesmo, e eram traduzidos
     por um ternário que só sabia dois casos. Com o terceiro campo virou mapa. */
  const CAMPO_ITEM = { qtd: "quantidade", valor: "valorUnitario", mec: "mecanico_anotado" };
  const mudarLinha = (iid, campo, valor) => {
    set((d) => ({ ...d, os: d.os.map((o) => (o.id === os.id
      ? { ...o, itens: o.itens.map((i) => (i.id === iid ? { ...i, [campo]: valor } : i)) } : o)) }));
    clearTimeout(salvarItemDepois.current[iid]);
    salvarItemDepois.current[iid] = setTimeout(() => {
      acoes.atualizarItemOS(os._id ?? os.id, iid, { [CAMPO_ITEM[campo]]: valor })
        .catch((e) => toast("Não salvou o item", e?.message ?? "A alteração ficou só nesta tela.", "alerta"));
    }, 700);
  };

  const tirarLinha = (iid) => executar(
    () => acoes.removerItemOS(os._id ?? os.id, iid), null, "Não deu para remover o item");

  /* Cancelar exige motivo — o banco recusa com menos de 5 caracteres, e a
     oficina precisa saber depois por que aquele carro não virou serviço. */
  const [cancelando, setCancelando] = useState(false);
  const [motivoCancel, setMotivoCancel] = useState("");
  const [excluindo, setExcluindo] = useState(false);
  /* Rascunho da edição do veículo. Fica fora do db até salvar: enquanto a
     pessoa digita a placa, ela passa por estados inválidos, e o resto da
     tela não pode acompanhar isso. */
  const [editVeic, setEditVeic] = useState(null);
  const [trocaVeic, setTrocaVeic] = useState("");
  /* Campo numérico guarda número, porque desconto entra na conta do total.
     Mas número não é o que a pessoa digita: "1.250" passa por "1." e por
     "1.25" antes de ficar pronto, e converter a cada tecla apagava o ponto
     na frente de quem escrevia. Estes dois seguram o texto cru enquanto o
     campo está em uso, e soltam no blur. */
  /* O que o cliente vai pagar, escolhido na entrega. A aprovação já criou um
     a receber com o palpite da casa (pix, à vista); aqui é onde vira o
     combinado de verdade, com o cliente na frente do balcão. */
  const [concluindo, setConcluindo] = useState(false);
  const [pgto, setPgto] = useState({ forma: "pix", parcelas: 1, primeiro: 0, intervalo: 30 });

  const [rascKm, setRascKm] = useState(null);
  const [rascDesc, setRascDesc] = useState(null);

  /**
   * Excluir só vale enquanto a OS não virou nada — sem peça reservada, sem
   * título, sem nota. Depois disso o caminho é cancelar, que estorna o que
   * precisa e deixa registro. O servidor recusa de qualquer forma; esconder o
   * botão evita oferecer o que vai ser negado.
   */
  // `os` é null enquanto a lista está aberta — esta linha roda no corpo do
  // componente, antes do `if (os)`. Sem o encadeamento opcional, entrar na
  // aba de Ordens de serviço derrubava a tela inteira para branco.
  const podeExcluir = !!os && ["orcamento", "aguardando"].includes(os.status);
  const confirmarExclusao = () => executar(
    () => acoes.excluirOS(os._id ?? os.id),
    () => { setExcluindo(false); setAberta(null);
            toast("OS excluída", "Ela não tinha movimento nem título — não deixou rastro a preservar."); },
    "Não deu para excluir");
  const confirmarCancelamento = () => executar(
    () => acoes.cancelarOS(os._id ?? os.id, motivoCancel.trim()),
    () => { setCancelando(false); setMotivoCancel(""); setAberta(null);
            toast("OS cancelada", "Peça reservada foi devolvida ao estoque."); },
    "Não deu para cancelar");

  const aplicarVoz = (itens, saida) => {
    itens.forEach((i) => {
      const cat = i.tipo === "peca" ? db.pecas.find((p) => p.id === i.ref) : db.servicos.find((s) => s.id === i.ref);
      addLinha({ tipo: i.tipo, ref: i.ref || null, desc: cat ? cat.nome : i.desc, qtd: i.qtd, valor: cat ? (i.tipo === "peca" ? cat.venda : cat.valor) : i.valor, custo: cat && i.tipo === "peca" ? cat.custo : 0 });
    });
    if (saida?.km) mudarOS(os.id, (o) => ({ ...o, km: saida.km }));
    if (saida?.queixa) mudarOS(os.id, (o) => ({ ...o, queixa: o.queixa || saida.queixa }));
  };

  /* aprovação: baixa estoque + gera título a receber */
  /**
   * Aprovar agora acontece no servidor, numa transação só.
   *
   * Diferença que importa: ele RESERVA a peça em vez de baixar. A baixa é
   * na execução, porque entre aprovar e montar passam dias e peça de
   * cliente que desistiu precisa voltar inteira ao estoque.
   *
   * E ele recusa por estoque, dizendo qual peça faltou e quanto tem — a
   * checagem local que existia aqui não enxergava o que outra OS já
   * prometeu.
   */
  const aprovar = () => executar(
    () => acoes.aprovarOS(os._id ?? os.id, "presencial"),
    (r) => toast("Orçamento aprovado",
      `${r.pecasReservadas} peça(s) reservadas e ${brl(r.total)} lançado a receber.`),
    "Não deu para aprovar");

  const recusar = () => executar(
    () => acoes.recusarOS(os._id ?? os.id, "Cliente recusou o orçamento"),
    () => toast("Orçamento recusado", "Registrado. O veículo pode ser liberado.", "alerta"),
    "Não deu para recusar");

  /* Em que degrau a OS está. -1 para recusada, que não é degrau — e -1
     também quando não há OS aberta: este componente desenha a lista e o
     detalhe, e na lista `os` é null. Ler os.status aqui derrubava a aba
     inteira para branco antes de qualquer clique. */
  const etapa = os ? ETAPAS.findIndex(([e]) => e === os.status) : -1;

  const salvarVeiculo = () => executar(
    () => acoes.atualizarVeiculo(editVeic.id, editVeic),
    () => { setEditVeic(null); toast("Veículo corrigido", "O cadastro vale para todas as OS deste carro."); },
    "Não deu para salvar o veículo");

  const trocarVeiculo = () => executar(
    () => acoes.trocarVeiculoOS(os._id ?? os.id, trocaVeic),
    () => { setEditVeic(null); setTrocaVeic(""); toast("Veículo trocado", "A OS passou para o outro carro, e o dono foi junto."); },
    "Não deu para trocar o veículo");

  /* As parcelas como o servidor vai gerar: a sobra dos centavos toda na
     última, senão a soma não fecha com a nota. A conta está duplicada aqui
     de propósito — mostrar ao cliente um valor e gravar outro seria pior. */
  /* A soma dos itens, sem desconto nenhum. totalOS já abate o desconto que
     estiver gravado, então somar de volta dá o bruto — e é sobre o bruto que
     o desconto de fechamento é digitado. */
  const brutoOS = os ? totalOS(os) + (Number(os.desconto) || 0) : 0;
  const descontoFecha = Math.max(0, Math.min(brutoOS, numeroBR(pgto.desconto) ?? 0));
  const totalFecha = Math.max(0, brutoOS - descontoFecha);

  /* Parcelado é o contrário de recebido na hora: o que entra é uma parcela
     de cada vez, no vencimento de cada uma. */
  const aVista = !PARCELAVEIS.includes(pgto.forma) || Math.max(1, +pgto.parcelas || 1) === 1;

  const parcelasPrevistas = (() => {
    // Este componente desenha a lista e o detalhe; na lista `os` é null.
    if (!os) return [];
    const total = totalFecha;
    const n = PARCELAVEIS.includes(pgto.forma) ? Math.max(1, Math.min(48, +pgto.parcelas || 1)) : 1;
    const base = Math.floor((total / n) * 100) / 100;
    const hoje = new Date();
    return Array.from({ length: n }, (_, i) => {
      const venc = new Date(hoje);
      venc.setDate(venc.getDate() + (+pgto.primeiro || 0) + i * (+pgto.intervalo || 30));
      return {
        n: i + 1,
        valor: i === n - 1 ? Number((total - base * (n - 1)).toFixed(2)) : base,
        vence: venc.toISOString().slice(0, 10),
      };
    });
  })();

  const concluir = () => executar(
    () => acoes.finalizarOS(os._id ?? os.id, undefined, {
      forma: pgto.forma,
      parcelas: PARCELAVEIS.includes(pgto.forma) ? Math.max(1, +pgto.parcelas || 1) : 1,
      primeiroVencimentoDias: +pgto.primeiro || 0,
      intervaloDias: +pgto.intervalo || 30,
      desconto: descontoFecha,
      recebido: aVista && !!pgto.recebido,
    }),
    () => {
      setConcluindo(false);
      const nome = FORMAS.find(([v]) => v === pgto.forma)?.[1] ?? pgto.forma;
      const abateu = descontoFecha > 0 ? ` ${brl(descontoFecha)} de desconto aplicado.` : "";
      const baixou = aVista && pgto.recebido ? " Recebimento já registrado." : "";
      toast("Serviço concluído",
        parcelasPrevistas.length > 1
          ? `${nome} em ${parcelasPrevistas.length}x · primeira vence ${fdata(parcelasPrevistas[0].vence)}.`
          : `${nome} · ${brl(totalFecha)}.${baixou}${abateu}`);
    },
    "Não deu para concluir");

  const voltarAoOrcamento = () => executar(
    () => acoes.reabrirOS(os._id ?? os.id),
    () => toast("Voltou para orçamento", "Dá para mexer nos itens e aprovar de novo."),
    "Não deu para voltar");

  const lista = db.os.filter((o) => {
    if (filtro !== "todas" && o.status !== filtro) return false;
    const t = q.toLowerCase();
    if (!t) return true;
    const v = vei(o.vei), c = cli(o.cli);
    return o.id.toLowerCase().includes(t) || c?.nome.toLowerCase().includes(t) || v?.placa.toLowerCase().includes(t.replace(/[^a-z0-9]/gi, "")) || v?.modelo.toLowerCase().includes(t);
  });

  /* ── EDITOR ── */
  if (os) {
    const v = vei(os.vei), c = cli(os.cli);
    const pecas = os.itens.filter((i) => i.tipo === "peca"), servs = os.itens.filter((i) => i.tipo === "servico");
    const subP = pecas.reduce((s, i) => s + i.qtd * i.valor, 0), subS = servs.reduce((s, i) => s + i.qtd * i.valor, 0);
    const tot = totalOS(os), cst = custoOS(os), lucro = tot - cst;
    /* Dois travamentos diferentes, porque o servidor tem dois.

       "editavel" guarda o que o cliente aceitou: queixa, desconto, itens e
       qual carro. Muda só antes da aprovação.

       "emAberto" guarda o que é operação da oficina: quem vai fazer, quanto
       marcava o hodômetro, que dia o carro entrou. Vale até a OS encerrar.

       A tela travava os dois juntos na aprovação, e com isso não dava para
       designar mecânico numa OS aprovada — que é exatamente quando se
       designa. O servidor sempre deixou. */
    const editavel = ["orcamento", "aguardando"].includes(os.status);
    const emAberto = !["entregue", "recusada"].includes(os.status);
    const ck = db.checklists.find((x) => x.id === os.checklist);

    const linha = (i) => {
      const p = i.ref && i.tipo === "peca" ? db.pecas.find((x) => x.id === i.ref) : null;
      return (
        <tr key={i.id}>
          <td>
            <div className="text-sm font-medium">{i.desc}</div>
            <div className="text-xs t-mut2 mono flex items-center gap-2">
              {i.ref ? (p ? p.cod : db.servicos.find((s) => s.id === i.ref)?.cod) : "sem código"}
              {p && p.qtd < i.qtd && <span className="t-crit">estoque insuficiente ({p.qtd} disp.)</span>}
            </div>
            {/* Quem fez este serviço. Vale até a OS encerrar, e não só até a
                aprovação: quem alinhou só se sabe depois de alinhar. Em peça
                e taxa não aparece — não há execução a atribuir. */}
            {["servico", "terceirizado"].includes(i.tipo) && (emAberto || i.mec) && (
              <input className="inp" style={{ padding: "2px 7px", fontSize: 12, marginTop: 4, maxWidth: 190 }}
                value={i.mec ?? ""} disabled={!emAberto} placeholder="Quem fez"
                onChange={(e) => mudarLinha(i.id, "mec", e.target.value)} />
            )}
          </td>
          <td style={{ width: 78 }}>
            {editavel ? <input className="inp inp-mono" style={{ padding: "4px 7px", fontSize: 13, textAlign: "right" }} value={i.qtd} onChange={(e) => mudarLinha(i.id, "qtd", Math.max(1, +e.target.value || 1))} />
              : <span className="mono text-sm">{i.qtd}</span>}
          </td>
          <td style={{ width: 108 }}>
            {editavel ? <input className="inp inp-mono" style={{ padding: "4px 7px", fontSize: 13, textAlign: "right" }} value={i.valor} onChange={(e) => mudarLinha(i.id, "valor", +e.target.value || 0)} />
              : <span className="mono text-sm">{brl(i.valor)}</span>}
          </td>
          {admin && <td className="num text-sm t-mut">{i.custo ? brl(i.custo * i.qtd) : "—"}</td>}
          <td className="num text-sm font-semibold">{brl(i.qtd * i.valor)}</td>
          <td style={{ width: 36 }}>{editavel && <button className="btn btn-g btn-xs" onClick={() => tirarLinha(i.id)}><Trash2 size={13} /></button>}</td>
        </tr>
      );
    };

    return (
      <div className="grid gap-4">
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="stripe" />
          <div className="p-4 flex flex-wrap items-center gap-4">
            <Btn variant="g" size="xs" icon={ChevronRight} onClick={() => setAberta(null)} className="order-first" style={{ transform: "rotate(180deg)" }} />
            <Placa n={v?.placa || ""} size="lg" />
            <div className="flex-1" style={{ minWidth: 190 }}>
              <div className="flex items-center gap-2.5">
                <span className="dsp mono" style={{ fontSize: 20 }}>{os.id}</span>
                <Pill t={ST[os.status].c.replace("p-", "")}>{ST[os.status].l}</Pill>
              </div>
              <div className="text-sm t-mut mt-0.5 flex flex-wrap items-center gap-2">
                <span>{v?.marca} {v?.modelo} · {c?.nome}</span>
                {/* Até aqui não havia como corrigir placa em lugar nenhum do
                    app: cadastrou errado, criava outro carro, e o histórico
                    do veículo nascia partido em dois. */}
                <Btn variant="g" size="xs" icon={Pencil}
                  onClick={() => { setEditVeic(v ? { ...v } : {}); setTrocaVeic(""); }}>Editar veículo</Btn>
              </div>
            </div>
            <div className="text-right">
              <div className="eyebrow">Total do orçamento</div>
              <div className="dsp mono" style={{ fontSize: 26 }}>{brl(tot)}</div>
            </div>
          </div>
          <div className="px-4 pb-4 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))" }}>
            {/* Campo escrito, não lista de cadastrados. Quem monta o motor
                nem sempre tem login: é o contratado da semana, o filho do
                dono que ajuda na terça, o especialista em injeção que vem
                quando precisa. Obrigar cadastro de usuário para escrever um
                nome era pedir que a oficina mudasse o jeito de trabalhar
                para caber no programa. O vínculo com usuário continua no
                banco para quem tem, e a garantia segue usando. */}
            <Field label="Mecânico responsável">
              <input className="inp" value={os.mecNome ?? ""} disabled={!emAberto}
                placeholder="Quem fez o serviço"
                onChange={(e) => mudarOS(os.id, (o) => ({ ...o, mecNome: e.target.value }))} />
            </Field>
            <Field label="Hodômetro">
              <input className="inp inp-mono" inputMode="numeric" value={rascKm ?? os.km} disabled={!emAberto}
                onChange={(e) => { setRascKm(e.target.value); mudarOS(os.id, (o) => ({ ...o, km: inteiroBR(e.target.value) ?? 0 })); }}
                onBlur={() => setRascKm(null)} />
            </Field>
            <Field label="Entrada"><input className="inp" type="date" value={os.abertura} onChange={(e) => mudarOS(os.id, (o) => ({ ...o, abertura: e.target.value }))} disabled={!emAberto} /></Field>
            <Field label="Previsão de entrega"><input className="inp" type="date" value={os.previsao} onChange={(e) => mudarOS(os.id, (o) => ({ ...o, previsao: e.target.value }))} disabled={!emAberto} /></Field>
          </div>
        </div>

        <div className="card p-4">
          <Field label="Queixa do cliente">
            {/* O servidor recusa queixa depois da aprovação: é o que o
                cliente aceitou. A tela deixava digitar assim mesmo, e a
                pessoa só descobria pelo aviso de "não salvou". */}
            <textarea className="inp" rows={2} value={os.queixa} disabled={!editavel} onChange={(e) => mudarOS(os.id, (o) => ({ ...o, queixa: e.target.value }))} placeholder="O que o cliente relatou, nas palavras dele." />
          </Field>
          {ck && (
            <div className="flex items-center gap-2 mt-3 px-3 py-2 text-sm" style={{ background: "var(--gulf-l)", border: "1px solid #BEE0EC", borderRadius: "var(--r)" }}>
              <ClipboardCheck size={15} className="t-gulf" />
              Checklist <span className="mono font-semibold">{ck.id}</span> vinculado · {Object.values(ck.itens).filter((x) => x === "crit").length} item(ns) crítico(s) · {ck.avarias.length} avaria(s)
            </div>
          )}
        </div>

        {/* itens */}
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
            <span className="dsp" style={{ fontSize: 15 }}>Itens do orçamento</span>
            {editavel && (
              <div className="flex flex-wrap gap-2">
                <Btn variant="s" size="xs" icon={Mic} onClick={() => setVoz(true)}>Ditar por voz</Btn>
                <Btn variant="o" size="xs" icon={Package} onClick={() => { setAddItem("peca"); setBuscaItem(""); }}>Adicionar peça</Btn>
                <Btn variant="o" size="xs" icon={Wrench} onClick={() => { setAddItem("servico"); setBuscaItem(""); }}>Adicionar serviço</Btn>
              </div>
            )}
          </div>
          {os.itens.length === 0 ? (
            <Vazio icon={Wrench} titulo="Orçamento em branco" msg="Adicione peças e serviços pela busca, ou dite tudo por voz enquanto trabalha no veículo."
              acao={<Btn variant="s" icon={Mic} onClick={() => setVoz(true)}>Ditar por voz</Btn>} />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="tbl">
                <thead><tr><th>Descrição</th><th style={{ textAlign: "right" }}>Qtd</th><th style={{ textAlign: "right" }}>Unitário</th>{admin && <th className="num">Custo</th>}<th className="num">Total</th><th></th></tr></thead>
                <tbody>
                  {pecas.length > 0 && <tr><td colSpan={admin ? 6 : 5} className="eyebrow" style={{ background: "var(--el)", padding: "6px 12px" }}>Peças</td></tr>}
                  {pecas.map((i) => linha(i))}
                  {servs.length > 0 && <tr><td colSpan={admin ? 6 : 5} className="eyebrow" style={{ background: "var(--el)", padding: "6px 12px" }}>Serviços / mão de obra</td></tr>}
                  {servs.map((i) => linha(i))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-4 p-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", borderTop: "1px solid var(--line)", background: "var(--el)" }}>
            <div className="grid gap-1.5 text-sm">
              <div className="flex justify-between"><span className="t-mut">Peças</span><span className="mono">{brl(subP)}</span></div>
              <div className="flex justify-between"><span className="t-mut">Serviços</span><span className="mono">{brl(subS)}</span></div>
              <div className="flex justify-between items-center">
                <span className="t-mut">Desconto</span>
                <input className="inp inp-mono" inputMode="decimal" style={{ width: 100, padding: "3px 8px", fontSize: 13, textAlign: "right" }}
                  value={rascDesc ?? os.desconto} disabled={!editavel}
                  onChange={(e) => { setRascDesc(e.target.value); mudarOS(os.id, (o) => ({ ...o, desconto: numeroBR(e.target.value) ?? 0 })); }}
                  onBlur={() => setRascDesc(null)} />
              </div>
              <div className="flex justify-between pt-1.5" style={{ borderTop: "1px solid var(--line2)" }}>
                <span className="dsp" style={{ fontSize: 15 }}>Total</span><span className="mono font-semibold" style={{ fontSize: 17 }}>{brl(tot)}</span>
              </div>
            </div>
            {admin && (
              <div className="grid gap-1.5 text-sm">
                <div className="eyebrow">Margem — visível só para você</div>
                <div className="flex justify-between"><span className="t-mut">Custo das peças</span><span className="mono">{brl(cst)}</span></div>
                <div className="flex justify-between"><span className="t-mut">Lucro bruto</span><span className="mono t-ok font-semibold">{brl(lucro)}</span></div>
                <div className="flex justify-between"><span className="t-mut">Margem</span><span className="mono font-semibold">{tot ? ((lucro / tot) * 100).toFixed(1) : "0,0"}%</span></div>
              </div>
            )}
          </div>
        </div>

        {/* ações */}
        <div className="card p-4">
          <div className="eyebrow mb-3">Andamento</div>
          {os.status !== "recusada" && (
            <div className="of-regua">
              {ETAPAS.map(([id, nome], i) => (
                <span key={id} className={"of-etapa" + (i < etapa ? " passou" : i === etapa ? " agora" : "")}>{nome}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {os.status === "orcamento" && <>
              {/* O cliente da oficina costuma estar no balcão: aprovar na
                  hora é o caminho mais comum, e o servidor aceita
                  orcamento -> aprovada direto. */}
              <Btn variant="ok" icon={CheckCircle2} disabled={ocupado} onClick={aprovar}>Aprovar orçamento</Btn>
              {/* Abre a conversa antes de mover: a OS não pode ficar
                  "aguardando cliente" se a mensagem não chegou a existir.
                  E quem move é o servidor — até hoje isto chamava
                  mudarOS(status), que só salva campos, então a etapa vivia
                  neste navegador e sumia no recarregamento seguinte. */}
              <Btn variant="s" icon={Send} disabled={ocupado} onClick={() => {
                const texto = [
                  `*${db.oficina?.nome_fantasia || db.oficina?.razao_social || "Oficina"}*`,
                  `Orçamento da ${os.id} — ${v?.marca ?? ""} ${v?.modelo ?? ""} ${v?.placa ? `(${v.placa})` : ""}`.trim(),
                  ``, `Total: ${brl(totalOS(os))}`, ``,
                  `Pode responder por aqui aprovando ou tirando dúvida.`,
                ].join("\n");
                if (!abrirWhatsApp(c?.tel, texto, toast)) return;
                executar(() => acoes.enviarOS(os._id ?? os.id),
                  () => toast("WhatsApp aberto", "Confira a mensagem e aperte enviar por lá. A OS ficou aguardando o cliente."),
                  "A mensagem abriu, mas a OS não mudou de etapa");
              }}>Enviar ao cliente pelo WhatsApp</Btn>
              {/* Nem tudo passa por mensagem: muita coisa é combinada por
                  telefone ou no balcão, e a etapa precisa acompanhar. */}
              <Btn variant="o" icon={Timer} disabled={ocupado}
                onClick={() => executar(() => acoes.enviarOS(os._id ?? os.id),
                  () => toast("Aguardando o cliente", "A OS saiu do orçamento e está esperando resposta."),
                  "Não deu para mudar de etapa")}>Marcar como aguardando</Btn>
              <Btn variant="x" icon={XCircle} disabled={ocupado} onClick={recusar}>Cliente recusou</Btn>
              <Btn variant="o" icon={Printer} onClick={() => window.print()}>Imprimir orçamento</Btn>
            </>}
            {os.status === "aguardando" && <>
              <Btn variant="ok" icon={CheckCircle2} disabled={ocupado} onClick={aprovar}>Registrar aprovação do cliente</Btn>
              <Btn variant="o" icon={MessageCircle} onClick={() => abrirWhatsApp(c?.tel,
                `Olá! Passando para saber se ficou alguma dúvida sobre o orçamento da ${os.id}.`, toast)}>Cobrar retorno</Btn>
              {/* O cliente pediu para tirar um item, ou mudou o serviço. Sem
                  isto a OS ficava presa esperando um sim ou um não. */}
              <Btn variant="o" icon={RefreshCw} disabled={ocupado} onClick={voltarAoOrcamento}>Voltar para orçamento</Btn>
              <Btn variant="x" icon={XCircle} disabled={ocupado} onClick={recusar}>Cliente recusou</Btn>
            </>}
            {os.status === "aprovada" && <Btn variant="p" icon={Wrench} disabled={ocupado}
              onClick={() => executar(() => acoes.executarOS(os._id ?? os.id),
                (r) => toast("Serviço iniciado", `Peças baixadas do estoque · custo ${brl(r?.custoTotal ?? 0)}.`),
                "Não deu para iniciar")}>Iniciar execução</Btn>}
            {os.status === "execucao" && <Btn variant="ok" icon={Check} disabled={ocupado}
              onClick={() => { setPgto({ forma: "pix", parcelas: 1, primeiro: 0, intervalo: 30, desconto: String(os.desconto ?? 0), recebido: true }); setConcluindo(true); }}>Concluir serviço</Btn>}
            {os.status === "finalizada" && <>
              {!os.nfServico && subS > 0 && <Btn variant="p" icon={FileText} onClick={() => emitirNF(os, "nfse")}>Emitir NFS-e (serviços)</Btn>}
              {!os.nfPeca && subP > 0 && <Btn variant="p" icon={Receipt} onClick={() => emitirNF(os, "nfe")}>Emitir NF-e (peças)</Btn>}
              <Btn variant="ok" icon={Truck} disabled={ocupado}
                onClick={() => executar(() => acoes.entregarOS(os._id ?? os.id),
                  () => toast("Veículo entregue", "OS encerrada e lembrete de revisão agendado."),
                  "Não deu para entregar")}>Entregar veículo</Btn>
            </>}
            {/* Agendar exige alguém enviando depois, e não há isso ainda.
                Dizer "agendado" faria a oficina contar com um retorno que
                nunca vai sair. */}
            {os.status === "entregue" && <Btn variant="o" icon={MessageCircle}
              onClick={() => toast("Agendamento ainda não disponível",
                "Nada é enviado no futuro por enquanto. Para falar agora, use o WhatsApp na ficha do cliente.", "alerta")}>Agendar lembrete de revisão</Btn>}
            {os.status === "recusada" && <Btn variant="o" icon={RefreshCw} disabled={ocupado}
              onClick={() => executar(() => acoes.reabrirOS(os._id ?? os.id),
                () => toast("Orçamento reaberto", "Dá para editar os itens e aprovar de novo."),
                "Não deu para reabrir")}>Reabrir orçamento</Btn>}
            <Btn variant="o" icon={Printer} onClick={() => window.print()}>Imprimir / PDF</Btn>
            {podeExcluir && (
              <Btn variant="x" icon={Trash2} disabled={ocupado} onClick={() => setExcluindo(true)}>Excluir OS</Btn>
            )}
            {!["entregue", "recusada"].includes(os.status) && (
              <Btn variant="x" icon={Trash2} disabled={ocupado} onClick={() => setCancelando(true)}>Cancelar OS</Btn>
            )}
          </div>
          <div className="flex flex-wrap gap-4 mt-4 pt-3 text-xs t-mut" style={{ borderTop: "1px solid var(--line)" }}>
            <span>Aberta em {fdata(os.abertura)}</span>
            {os.aprovadoEm && <span>Aprovada em {fdata(os.aprovadoEm)}</span>}
            {os.nfServico && <span className="mono">NFS-e {os.nfServico}</span>}
            {os.nfPeca && <span className="mono">NF-e {os.nfPeca}</span>}
          </div>
        </div>

        <VozOrcamento open={voz} onClose={() => setVoz(false)} db={db} aplicar={aplicarVoz} toast={toast} mecanico={{ nome: os.mecNome }} />

        {/* Folha de impressão. Escondida na tela; o @media print mostra só ela. */}
        <div className="folha">
          <CabecalhoFolha of={db.oficina} titulo="Ordem de serviço"
            numero={os.id} data={`Aberta em ${fdata(os.abertura)}`} />

          <h2>Cliente e veículo</h2>
          <div className="par">
            <div><b>Cliente:</b> {c?.nome ?? "—"}</div>
            <div><b>Telefone:</b> {c?.tel || "—"}</div>
            <div><b>Veículo:</b> {[v?.marca, v?.modelo].filter(Boolean).join(" ") || "—"}</div>
            <div><b>Placa:</b> {v?.placa ?? "—"}</div>
            <div><b>Hodômetro na entrada:</b> {os.km ? `${os.km} km` : "—"}</div>
            <div><b>Previsão de entrega:</b> {os.previsao ? fdata(os.previsao) : "a combinar"}</div>
            {/* Quem executou. A oficina pediu no papel: sem isto, a folha
                arquivada não diz quem montou o motor, e na garantia de seis
                meses ninguém lembra. O campo é escrito à mão, então vale
                tanto para o funcionário quanto para o contratado da semana. */}
            <div><b>Mecânico responsável:</b> {os.mecNome || "—"}</div>
          </div>

          {os.queixa && <><h2>Queixa do cliente</h2><div className="obs">{os.queixa}</div></>}

          <ItensFolha itens={os.itens} desconto={os.desconto} />

          {/* Como foi combinado o pagamento.
              Só aparece depois de aprovada, que é quando os títulos nascem.
              Antes disso não existe combinação nenhuma, e imprimir "forma:
              —" num documento que o cliente leva embora é pior que não
              imprimir o bloco: parece que ficou de graça. */}
          {os.pagamento?.length > 0 && <>
            <h2>Pagamento</h2>
            <table>
              <thead><tr>
                <th style={{ width: "13%" }}>Parcela</th>
                <th>Forma</th>
                <th style={{ width: "17%" }}>Vencimento</th>
                <th style={{ width: "22%" }}>Situação</th>
                <th className="n" style={{ width: "17%" }}>Valor</th>
              </tr></thead>
              <tbody>
                {os.pagamento.map((p) => (
                  <tr key={p.id}>
                    <td>{p.parcelas > 1 ? `${p.parcela}/${p.parcelas}` : "À vista"}</td>
                    <td>{ROTULO_FORMA[p.forma] ?? p.forma ?? "a combinar"}</td>
                    <td>{p.venc ? fdata(p.venc) : "—"}</td>
                    <td>{p.pago ? `Pago em ${fdata(p.pago)}` : "Em aberto"}</td>
                    <td className="n">{brl(p.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>}

          <div className="ass">
            <div>Assinatura do cliente</div>
            <div>Responsável pela oficina</div>
          </div>

          <div className="rodape">
            Peças e serviços têm garantia conforme a legislação e as condições informadas no balcão.
            Este documento não é nota fiscal.
          </div>
        </div>

        <Modal open={concluindo} onClose={() => setConcluindo(false)}
          title="Como o cliente vai pagar?"
          sub="O serviço está pronto. Isto define os títulos a receber desta OS."
          footer={<>
            <Btn variant="g" onClick={() => setConcluindo(false)}>Voltar</Btn>
            <Btn variant="ok" icon={Check} disabled={ocupado} onClick={concluir}>Concluir serviço</Btn>
          </>}>
          <div className="grid gap-4">
            <Field label="Forma de pagamento">
              <select className="inp" value={pgto.forma}
                onChange={(e) => setPgto((p) => ({
                  ...p, forma: e.target.value,
                  // Boleto sem prazo não é boleto; à vista vence hoje.
                  parcelas: PARCELAVEIS.includes(e.target.value) ? p.parcelas : 1,
                  primeiro: e.target.value === "boleto" ? (p.primeiro || 10) : 0,
                  recebido: NA_HORA.includes(e.target.value),
                }))}>
                {FORMAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>

            {PARCELAVEIS.includes(pgto.forma) && (
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))" }}>
                <Field label="Parcelas">
                  <input className="inp inp-mono" inputMode="numeric" value={pgto.parcelas}
                    onChange={(e) => setPgto((p) => ({ ...p, parcelas: e.target.value }))} />
                </Field>
                <Field label="Primeiro vencimento (dias)">
                  <input className="inp inp-mono" inputMode="numeric" value={pgto.primeiro}
                    onChange={(e) => setPgto((p) => ({ ...p, primeiro: e.target.value }))} />
                </Field>
                <Field label="Intervalo entre parcelas (dias)">
                  <input className="inp inp-mono" inputMode="numeric" value={pgto.intervalo}
                    onChange={(e) => setPgto((p) => ({ ...p, intervalo: e.target.value }))} />
                </Field>
              </div>
            )}

            {/* O desconto do fechamento. O orçamento foi aprovado num valor;
                aqui a oficina cede na frente do cliente, que é quando essa
                conversa acontece. Quem digita está olhando o que vai cobrar,
                então a conta aparece inteira embaixo: bruto, desconto, total. */}
            <Field label="Desconto no fechamento (R$)">
              <input className="inp inp-mono" inputMode="decimal" value={pgto.desconto ?? "0"}
                onChange={(e) => setPgto((p) => ({ ...p, desconto: e.target.value }))} />
            </Field>

            <div style={{ borderTop: "1px solid var(--line)", paddingTop: 10 }}>
              <div className="flex items-center justify-between text-sm t-mut">
                <span>Soma dos itens</span><span className="mono">{brl(brutoOS)}</span>
              </div>
              {descontoFecha > 0 && (
                <div className="flex items-center justify-between text-sm t-ok">
                  <span>Desconto</span><span className="mono">− {brl(descontoFecha)}</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-1" style={{ fontSize: 17 }}>
                <span className="font-semibold">Total a pagar</span>
                <span className="mono font-semibold">{brl(totalFecha)}</span>
              </div>
              {(numeroBR(pgto.desconto) ?? 0) > brutoOS && (
                <div className="text-sm t-signal mt-1">
                  O desconto é maior que a soma dos itens. Vai valer {brl(brutoOS)}.
                </div>
              )}
            </div>

            {/* A oficina acumulou 36 títulos "vencidos" somando R$ 7.397 em
                duas semanas — 20 deles pix à vista, o mais velho de um mês.
                Pix não fica um mês vencido: o dinheiro estava no caixa e
                ninguém tinha ido ao financeiro dar baixa. Aqui a baixa
                acontece no mesmo gesto em que o carro sai. */}
            {aVista && (
              <label className="flex items-center gap-2" style={{ cursor: "pointer" }}>
                <input type="checkbox" checked={!!pgto.recebido}
                  onChange={(e) => setPgto((p) => ({ ...p, recebido: e.target.checked }))} />
                <span className="text-sm">
                  Já recebi este valor — dar baixa agora
                </span>
              </label>
            )}

            <div>
              <div className="eyebrow mb-2">O que vai ser lançado</div>
              <table style={{ width: "100%", fontSize: 14 }}>
                <tbody>
                  {parcelasPrevistas.map((p) => (
                    <tr key={p.n}>
                      <td style={{ padding: "3px 0" }} className="t-mut">
                        {parcelasPrevistas.length > 1 ? `Parcela ${p.n}/${parcelasPrevistas.length}` : "À vista"}
                      </td>
                      <td style={{ padding: "3px 0" }} className="t-mut">vence {fdata(p.vence)}</td>
                      <td style={{ padding: "3px 0", textAlign: "right" }} className="mono font-semibold">{brl(p.valor)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "1px solid var(--line)" }}>
                    <td colSpan={2} style={{ paddingTop: 6 }}>Total</td>
                    <td style={{ paddingTop: 6, textAlign: "right" }} className="mono font-semibold">{brl(totalOS(os))}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm t-mut">
              Dá para mudar depois no financeiro, enquanto nada tiver sido recebido.
            </p>
          </div>
        </Modal>

        <Modal open={!!editVeic} onClose={() => setEditVeic(null)}
          title="Veículo desta OS"
          sub="Corrigir os dados vale para todas as OS deste carro. Trocar o veículo muda só esta OS."
          footer={<Btn variant="g" onClick={() => setEditVeic(null)}>Fechar</Btn>}>
          <div className="grid gap-4">
            {editVeic?.id && (
              <div className="grid gap-3">
                <div className="eyebrow">Corrigir os dados deste carro</div>
                <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))" }}>
                  <Field label="Placa">
                    <input className="inp inp-mono" maxLength={8} value={editVeic.placa ?? ""}
                      onChange={(e) => setEditVeic((x) => ({ ...x, placa: e.target.value.toUpperCase() }))} />
                  </Field>
                  <Field label="Marca">
                    <input className="inp" value={editVeic.marca ?? ""}
                      onChange={(e) => setEditVeic((x) => ({ ...x, marca: e.target.value }))} />
                  </Field>
                  <Field label="Modelo">
                    <input className="inp" value={editVeic.modelo ?? ""}
                      onChange={(e) => setEditVeic((x) => ({ ...x, modelo: e.target.value }))} />
                  </Field>
                  <Field label="Ano">
                    <input className="inp inp-mono" value={editVeic.ano ?? ""}
                      onChange={(e) => setEditVeic((x) => ({ ...x, ano: e.target.value }))} />
                  </Field>
                  <Field label="Cor">
                    <input className="inp" value={editVeic.cor ?? ""}
                      onChange={(e) => setEditVeic((x) => ({ ...x, cor: e.target.value }))} />
                  </Field>
                  <Field label="Hodômetro">
                    <input className="inp inp-mono" value={editVeic.km ?? ""}
                      onChange={(e) => setEditVeic((x) => ({ ...x, km: e.target.value }))} />
                  </Field>
                </div>
                <p className="text-sm t-mut">
                  A placa vai no formato ABC1D23 ou ABC1234 — fora disso o servidor recusa e diz
                  o porquê. A quilometragem pode ser escrita como 81.961, do jeito que se escreve.
                </p>
                <div><Btn variant="p" icon={Check} disabled={ocupado} onClick={salvarVeiculo}>Salvar dados do veículo</Btn></div>
              </div>
            )}

            <div className="grid gap-3" style={{ borderTop: "1px solid var(--line)", paddingTop: 14 }}>
              <div className="eyebrow">Foi aberta no carro errado?</div>
              {editavel ? (
                <>
                  <Field label="Passar esta OS para outro veículo">
                    <select className="inp" value={trocaVeic} onChange={(e) => setTrocaVeic(e.target.value)}>
                      <option value="">Escolha o veículo…</option>
                      {db.veiculos.filter((x) => x.id !== v?.id).map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.placa} — {x.marca} {x.modelo} · {cli(x.cli)?.nome ?? "sem dono"}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <p className="text-sm t-mut">O cliente da OS passa a ser o dono do carro escolhido.</p>
                  <div><Btn variant="o" icon={RefreshCw} disabled={ocupado || !trocaVeic} onClick={trocarVeiculo}>Trocar o veículo desta OS</Btn></div>
                </>
              ) : (
                <p className="text-sm t-mut">
                  Só antes da aprovação. Depois há peça reservada e um orçamento que o cliente
                  aceitou para este carro — reabra o orçamento para trocar.
                </p>
              )}
            </div>
          </div>
        </Modal>

        <Modal open={excluindo} onClose={() => setExcluindo(false)}
          title="Excluir esta ordem de serviço?"
          sub="Ela some da lista e não deixa registro. Use para OS aberta por engano ou duplicada."
          footer={<>
            <Btn variant="g" onClick={() => setExcluindo(false)}>Voltar</Btn>
            <Btn variant="x" icon={Trash2} disabled={ocupado} onClick={confirmarExclusao}>Excluir de vez</Btn>
          </>}>
          <div className="text-sm">
            <p className="mb-2"><strong>{os.id}</strong> — {c?.nome ?? "sem cliente"} · {v?.placa ?? "sem placa"}</p>
            <p className="t-mut">
              Excluir é diferente de cancelar. <strong>Cancelar</strong> é para OS que já aconteceu:
              devolve peça reservada, estorna o que foi aplicado e fica no histórico com o motivo.
              <strong> Excluir</strong> é para OS que nunca virou nada — some sem deixar rastro,
              porque não há rastro a preservar.
            </p>
            <p className="t-mut mt-2">
              O servidor recusa se esta OS tiver movimentado estoque, gerado nota ou lançado título.
            </p>
          </div>
        </Modal>

        <Modal open={cancelando} onClose={() => setCancelando(false)}
          title="Cancelar esta ordem de serviço?"
          sub="Peça reservada volta ao estoque. Se o serviço já começou, o que foi aplicado é estornado por movimento — o razão não se apaga."
          footer={<>
            <Btn variant="g" onClick={() => setCancelando(false)}>Voltar</Btn>
            <Btn variant="x" icon={Trash2} disabled={ocupado || motivoCancel.trim().length < 5}
              onClick={confirmarCancelamento}>Cancelar a OS</Btn>
          </>}>
          <Field label="Por que está sendo cancelada?" hint="Fica no histórico. Mínimo 5 caracteres.">
            <textarea className="inp" rows={3} autoFocus value={motivoCancel}
              onChange={(e) => setMotivoCancel(e.target.value)}
              placeholder="Ex.: cliente desistiu do orçamento e levou o carro." />
          </Field>
        </Modal>

        {/* buscar peça ou serviço */}
        <Modal open={!!addItem} onClose={() => setAddItem(null)} title={addItem === "peca" ? "Adicionar peça" : "Adicionar serviço"}
          sub={addItem === "peca" ? "Preço e custo vêm do estoque automaticamente." : "Valores da tabela de mão de obra."}>
          <Busca v={buscaItem} set={setBuscaItem} ph={addItem === "peca" ? "Nome, código ou categoria…" : "Nome do serviço…"} />
          <div className="grid gap-1.5 mt-3" style={{ maxHeight: 340, overflowY: "auto" }}>
            {(addItem === "peca" ? db.pecas : db.servicos)
              .filter((x) => !buscaItem || (x.nome + x.cod + (x.cat || "")).toLowerCase().includes(buscaItem.toLowerCase()))
              .map((x) => (
                <button key={x.id} className="flex items-center gap-3 px-3 py-2.5 text-left" style={{ border: "1px solid var(--line)", borderRadius: "var(--r)", background: "var(--card)", cursor: "pointer" }}
                  onClick={() => {
                    addLinha(addItem === "peca"
                      ? { tipo: "peca", ref: x.id, desc: x.nome, qtd: 1, valor: x.venda, custo: x.custo }
                      : { tipo: "servico", ref: x.id, desc: x.nome, qtd: 1, valor: x.valor, custo: 0 });
                    setAddItem(null);
                  }}>
                  <span className="mono text-xs t-mut2" style={{ width: 92 }}>{x.cod}</span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium">{x.nome}</span>
                    <span className="block text-xs t-mut">{addItem === "peca" ? `${x.marca} · ${x.cat} · ${x.local}` : `${x.h} h de mão de obra`}</span>
                  </span>
                  {addItem === "peca" && <Pill t={x.qtd > x.min ? "ok" : x.qtd > 0 ? "warn" : "crit"}>{x.qtd} {x.un}</Pill>}
                  <span className="mono text-sm font-semibold" style={{ width: 86, textAlign: "right" }}>{brl(addItem === "peca" ? x.venda : x.valor)}</span>
                </button>
              ))}
          </div>
          <Btn variant="g" size="xs" icon={Plus} className="mt-3" onClick={() => { addLinha({ tipo: addItem, ref: null, desc: buscaItem || "Item avulso", qtd: 1, valor: 0, custo: 0 }); setAddItem(null); }}>
            Lançar item avulso {buscaItem && `“${buscaItem}”`}
          </Btn>
        </Modal>
      </div>
    );
  }

  /* ── LISTA ── */
  const abas = [["todas", "Todas"], ...Object.entries(ST).map(([k, v]) => [k, v.l])];
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Busca v={q} set={setQ} ph="Buscar por OS, cliente, placa ou modelo…" />
        {/* `semCliente()` era chamado aqui e nunca existiu em lugar nenhum:
            numa oficina sem cliente cadastrado, clicar derrubava a tela
            inteira para branco. É o caminho de toda oficina no primeiro dia. */}
        <Btn variant="s" icon={Mic} onClick={() => {
          const c = db.clientes[0];
          if (!c) {
            toast("Cadastre um cliente antes",
              "A OS por voz precisa de um cliente para anexar o ditado. Cadastre em Clientes e volte aqui.", "erro");
            return;
          }
          setRascunho({ cliId: c.id });
          setTimeout(() => setVoz(true), 300);
        }}>Nova OS por voz</Btn>
        <Btn variant="p" icon={Plus} onClick={abrirBalcao}>Nova OS</Btn>
      </div>

      <div className="flex gap-1 scroll" style={{ overflowX: "auto", borderBottom: "1px solid var(--line)" }}>
        {abas.map(([k, l]) => {
          const n = k === "todas" ? db.os.length : db.os.filter((o) => o.status === k).length;
          return <button key={k} className={`tab ${filtro === k ? "on" : ""}`} onClick={() => setFiltro(k)}>{l} <span className="mono t-mut2">{n}</span></button>;
        })}
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))" }}>
        {lista.map((o) => {
          const v = vei(o.vei), c = cli(o.cli);
          /* Iniciais do nome escrito na OS. Vinham do cadastro de usuário, e
             mecânico sem login ficava com o quadradinho vazio. */
          const inic = (o.mecNome || "").trim().split(/s+/).filter(Boolean)
            .slice(0, 2).map((p) => p[0]).join("").toUpperCase();
          const atrasadaOS = atrasado(o.previsao) && !["entregue", "recusada"].includes(o.status);
          return (
            <button key={o.id} onClick={() => setAberta(o.id)} className="card text-left fade" style={{ cursor: "pointer", overflow: "hidden" }}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2.5"><Placa n={v?.placa || ""} size="md" /></div>
                  <Pill t={ST[o.status].c.replace("p-", "")}>{ST[o.status].l}</Pill>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="mono text-sm font-semibold">{o.id}</span>
                  <span className="text-sm t-mut truncate">{v?.marca} {v?.modelo}</span>
                </div>
                <div className="text-sm t-mut mt-1" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: 38 }}>
                  {o.queixa || "Sem queixa registrada."}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid var(--line)" }}>
                  <span className="text-xs t-mut flex items-center gap-1.5">
                    <span className="mono flex items-center justify-center" style={{ width: 20, height: 20, borderRadius: 4, background: "var(--ink)", color: "#fff", fontSize: 9, fontWeight: 600 }}>{inic}</span>
                    {c?.nome.split(" ").slice(0, 2).join(" ")}
                  </span>
                  <span className="mono text-sm font-semibold">{brl(totalOS(o))}</span>
                </div>
                {atrasadaOS && <div className="flex items-center gap-1.5 mt-2 text-xs t-signal font-semibold"><Timer size={12} />Previsão vencida em {fdata(o.previsao)}</div>}
              </div>
            </button>
          );
        })}
      </div>
      {lista.length === 0 && <div className="card"><Vazio icon={Wrench} titulo="Nenhuma OS nessa situação" msg="Troque o filtro ou abra uma ordem de serviço nova." acao={<Btn variant="p" icon={Plus} onClick={abrirBalcao}>Nova OS</Btn>} /></div>}
      {/* Chamada de função, não <ModalBalcao />: componente declarado dentro de
          outro é recriado a cada render, o React remonta, e o campo de busca
          perde o foco a cada tecla digitada. */}
      {ModalBalcao()}
    </div>
  );
}

export { VozOrcamento, totalOS, custoOS, OrdensServico };
